import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigation } from "contexts/notificationsContext";
import { useAuth } from "shared/hooks/auth";
import { exportarDadosTarefas } from "services/modules/quadroTarefas";

type TipoPeriodo = "criadas" | "trabalhadas";

// ============================================
// INTERFACES
// ============================================

interface ModalExportarDadosProps {
  open: boolean;
  onClose: () => void;
}

// ============================================
// COMPONENTE
// ============================================

/**
 * Modal "Exportar Dados" (menu Tarefas): define o período (Data Início/Fim)
 * e gera o download do XLSX MOF_DADOS_EXTRAIDOS_TAREFAS_{inicio}_{fim}.xlsx
 */
export const ModalExportarDados = ({ open, onClose }: ModalExportarDadosProps) => {
  const { user } = useAuth();
  const { openNotification } = useNavigation();

  const idEmpresa = user?.idEmpresa || 0;

  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [tipoPeriodo, setTipoPeriodo] = useState<TipoPeriodo>("criadas");
  const [isExporting, setIsExporting] = useState(false);

  // Limpar campos ao abrir o modal
  useEffect(() => {
    if (open) {
      setDataInicio("");
      setDataFim("");
      setTipoPeriodo("criadas");
      setIsExporting(false);
    }
  }, [open]);

  const handleExportar = async () => {
    // Validações: campos obrigatórios
    if (!dataInicio || !dataFim) {
      openNotification({
        isOpen: true,
        message: "Informe a Data Início e a Data Fim do período",
        type: "warning",
      });
      return;
    }

    // Validação: início <= fim
    if (dataInicio > dataFim) {
      openNotification({
        isOpen: true,
        message: "A Data Início deve ser menor ou igual à Data Fim",
        type: "warning",
      });
      return;
    }

    if (!idEmpresa) {
      openNotification({
        isOpen: true,
        message: "Não foi possível identificar a empresa do usuário",
        type: "error",
      });
      return;
    }

    setIsExporting(true);
    try {
      await exportarDadosTarefas(idEmpresa, dataInicio, dataFim, tipoPeriodo);
      openNotification({
        isOpen: true,
        message: "Dados exportados com sucesso!",
        type: "success",
      });
      onClose();
    } catch (error: any) {
      openNotification({
        isOpen: true,
        message: error?.message || "Erro ao exportar os dados de tarefas",
        type: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (isExporting) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Exportar Dados</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <FormControl component="fieldset" disabled={isExporting}>
            <FormLabel component="legend">
              <Typography variant="caption" color="text.secondary">
                Tipo de período
              </Typography>
            </FormLabel>
            <RadioGroup
              row
              value={tipoPeriodo}
              onChange={(e) => setTipoPeriodo(e.target.value as TipoPeriodo)}
            >
              <FormControlLabel
                value="criadas"
                control={<Radio size="small" />}
                label="Tarefas criadas"
              />
              <FormControlLabel
                value="trabalhadas"
                control={<Radio size="small" />}
                label="Tarefas trabalhadas"
              />
            </RadioGroup>
          </FormControl>
          <TextField
            label="Data Início *"
            type="date"
            size="small"
            fullWidth
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={isExporting}
          />
          <TextField
            label="Data Fim *"
            type="date"
            size="small"
            fullWidth
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={isExporting}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit" disabled={isExporting}>
          CANCELAR
        </Button>
        <Button
          onClick={handleExportar}
          variant="contained"
          color="primary"
          disabled={isExporting}
          sx={{ minWidth: 120 }}
        >
          {isExporting ? <CircularProgress size={20} /> : "EXPORTAR"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
